/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import chatIcon from '../../assets/start_chat.svg';
import createGroupIcon from '../../assets/create-group.svg';
import logoutIcon from '../../assets/logout.svg';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import '../../styles/CometChatSelector/CometChatSelector.css';
import { CometChatJoinGroup } from '../CometChatJoinGroup/CometChatJoinGroup';
import CometChatCreateGroup from '../CometChatCreateGroup/CometChatCreateGroup';
import CometChatUserProfile from '../CometChatUserProfile/CometChatUserProfile';
import {
  CometChatButton,
  CometChatCallLogs,
  CometChatConversations,
  CometChatGroups,
  CometChatOption,
  CometChatUIKit,
  CometChatUIKitLoginListener,
  CometChatUsers,
  getLocalizedString,
  CometChatContextMenu,
  Placement,
} from '@cometchat/chat-uikit-react';
import { AppContext } from '../../context/AppContext';
import { useCometChatContext } from '../../context/CometChatContext';
import { CallLog } from '@cometchat/calls-sdk-javascript';

interface SelectorProps {
  group?: CometChat.Group;
  showJoinGroup?: boolean;
  activeTab?: string;
  activeItem?: CometChat.User | CometChat.Group | CometChat.Conversation | CometChat.Call | CallLog;
  onSelectorItemClicked?: (
    input: CometChat.User | CometChat.Group | CometChat.Conversation | CometChat.Call,
    type: string
  ) => void;
  onProtectedGroupJoin?: (group: CometChat.Group) => void;
  showCreateGroup?: boolean;
  setShowCreateGroup?: Dispatch<SetStateAction<boolean>>;
  onHide?: () => void;
  onNewChatClicked?: () => void;
  onGroupCreated?: (group: CometChat.Group) => void;
  onSearchClicked?: () => void;
  hideCreateGroupButton?: boolean;
}

const CometChatSelector = (props: SelectorProps) => {
  const {
    group,
    showJoinGroup,
    activeItem,
    activeTab,
    onSelectorItemClicked = () => {},
    onProtectedGroupJoin = () => {},
    showCreateGroup,
    setShowCreateGroup = () => {},
    onHide = () => {},
    onNewChatClicked = () => {},
    onGroupCreated = () => {},
    onSearchClicked = () => {},
    hideCreateGroupButton = true,
  } = props;

  // State management
  const [loggedInUser, setLoggedInUser] = useState<CometChat.User | null>();
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [menuKey, setMenuKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userStatus, setUserStatus] = useState<string>(CometChat.USER_STATUS.OFFLINE);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const { setAppState } = useContext(AppContext);
  const { chatFeatures, callFeatures } = useCometChatContext();
  const getLoggedInUser = CometChatUIKitLoginListener.getLoggedInUser();

  // Function to update user status
  const updateUserStatus = (user: CometChat.User) => {
    const status = user.getStatus();
    setUserStatus(status);
  };

  // Function to fetch latest user data from CometChat
  const refreshUserData = async (user: CometChat.User) => {
    try {
     const response = await fetch(`https://neitclem-connect.gratusinc.org/cometchat-backend/get_cometchat_user.php?uid=${user.getUid()}`);

      if (response.ok) {
        const userData = await response.json();
        if (userData.success && userData.data) {
          const refreshedUser = new CometChat.User(userData.data.uid);
          refreshedUser.setName(userData.data.name);
          refreshedUser.setAvatar(userData.data.avatar);
          
          // Get current user status from CometChat
          try {
            const currentUser = await CometChat.getUser(userData.data.uid);
            if (currentUser) {
              refreshedUser.setStatus(currentUser.getStatus());
              updateUserStatus(refreshedUser);
            }
          } catch (statusError) {
            // Could not get user status
          }
          
          setLoggedInUser(refreshedUser);
          setMenuKey(prev => prev + 1);
          return refreshedUser;
        }
      }
    } catch (error) {
      // Could not refresh user data
    }
    
    // Fallback: try to get status from original user
    if (user) {
      updateUserStatus(user);
    }
    
    return user;
  };

  // Initialize user data on component mount
  useEffect(() => {
    const currentUser = getLoggedInUser;
    
    if (currentUser) {
      setIsLoading(true);
      setLoggedInUser(currentUser);
      updateUserStatus(currentUser);
      
      // Fetch latest user data
      refreshUserData(currentUser).finally(() => {
        setIsLoading(false);
      });
    } else {
      setLoggedInUser(null);
      setUserStatus(CometChat.USER_STATUS.OFFLINE);
      setMenuKey(prev => prev + 1);
      setIsLoading(false);
    }
  }, [getLoggedInUser]);

  // Listen for user status changes
  useEffect(() => {
    const userListener = new CometChat.UserListener({
      onUserOnline: (onlineUser: CometChat.User) => {
        if (loggedInUser && onlineUser.getUid() === loggedInUser.getUid()) {
          setUserStatus(CometChat.USER_STATUS.ONLINE);
        }
      },
      onUserOffline: (offlineUser: CometChat.User) => {
        if (loggedInUser && offlineUser.getUid() === loggedInUser.getUid()) {
          setUserStatus(CometChat.USER_STATUS.OFFLINE);
        }
      },
    });

    CometChat.addUserListener('selector-user-listener', userListener);

    return () => {
      CometChat.removeUserListener('selector-user-listener');
    };
  }, [loggedInUser]);

  // Listen for CometChat login/logout events
  useEffect(() => {
    const loginListener = new CometChat.LoginListener({
      onLoginSuccess: (user: CometChat.User) => {
        setLoggedInUser(user);
        updateUserStatus(user);
        setMenuKey(prev => prev + 1);
        
        // Fetch fresh data after login
        setTimeout(() => {
          refreshUserData(user);
        }, 1000);
      },
      onLogoutSuccess: () => {
        setLoggedInUser(null);
        setUserStatus(CometChat.USER_STATUS.OFFLINE);
        setMenuKey(prev => prev + 1);
      },
    });

    CometChat.addLoginListener('selector-login-listener', loginListener);

    return () => {
      CometChat.removeLoginListener('selector-login-listener');
    };
  }, []);

  // Handle call icons visibility
  useEffect(() => {
    if (activeTab === 'calls') {
      const iframe = document.getElementById('cometchat-frame') as HTMLIFrameElement;
      const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const voiceCallIcons =
        iframeDoc?.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-audio') ||
        document.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-audio');
      const videoCallIcons =
        iframeDoc?.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-video') ||
        document.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-video');

      const toggleCallIcons = () => {
        const voiceCallIcons =
          iframeDoc?.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-audio') ||
          document.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-audio');
        const videoCallIcons =
          iframeDoc?.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-video') ||
          document.getElementsByClassName('cometchat-call-logs__list-item-trailing-view-video');
        
        if (callFeatures.voiceAndVideoCalling.oneOnOneVoiceCalling) {
          Array.from(voiceCallIcons).forEach((icon: any) => {
            icon.style.display = '';
          });
        } else {
          Array.from(voiceCallIcons).forEach((icon: any) => {
            icon.style.display = 'none';
          });
        }

        if (callFeatures.voiceAndVideoCalling.oneOnOneVideoCalling) {
          Array.from(videoCallIcons).forEach((icon: any) => {
            icon.style.display = '';
          });
        } else {
          Array.from(videoCallIcons).forEach((icon: any) => {
            icon.style.display = 'none';
          });
        }
      };

      if (voiceCallIcons.length === 0 && videoCallIcons.length === 0) {
        const interval = setInterval(() => {
          if (voiceCallIcons.length > 0 || videoCallIcons.length > 0) {
            clearInterval(interval);
            toggleCallIcons();
          }
        }, 1);

        return () => clearInterval(interval);
      } else {
        toggleCallIcons();
      }
    }
  }, [callFeatures, activeTab]);

  // Event handlers
  const handleUserUpdated = (updatedUser: CometChat.User) => {
    setLoggedInUser(updatedUser);
    setMenuKey(prev => prev + 1);
    
    // Update CometChat's internal state
    try {
      const currentLoggedInUser = CometChatUIKitLoginListener.getLoggedInUser();
      if (currentLoggedInUser) {
        currentLoggedInUser.setName(updatedUser.getName());
        currentLoggedInUser.setAvatar(updatedUser.getAvatar());
      }
    } catch (error) {
      // Could not update CometChat internal state
    }
    
    // Force re-render
    setTimeout(() => {
      setMenuKey(prev => prev + 1);
    }, 100);
  };

  const logOut = () => {
    // Show logout overlay immediately
    setIsLoggingOut(true);
    
    // Small delay to show the overlay before starting logout
    setTimeout(() => {
      CometChatUIKit.logout()
        .then(() => {
          // Clear state after successful logout
          setLoggedInUser(null);
          setUserStatus(CometChat.USER_STATUS.OFFLINE);
          setAppState({ type: 'resetAppState' });
          setMenuKey(prev => prev + 1);
          setIsLoggingOut(false);
        })
        .catch((error) => {
          // Even if logout fails, clear the local state
          setLoggedInUser(null);
          setUserStatus(CometChat.USER_STATUS.OFFLINE);
          setMenuKey(prev => prev + 1);
          setIsLoggingOut(false);
        });
    }, 300); // Small delay to show overlay
  };

  // Menu options
  const getMenuOptions = (): CometChatOption[] => {
    return [
      new CometChatOption({
        id: 'create-conversation',
        title: getLocalizedString('create_conversation'),
        iconURL: chatIcon,
        onClick: () => {
          onNewChatClicked();
        },
      }),
      new CometChatOption({
        id: 'log-out',
        title: getLocalizedString('log_out'),
        iconURL: logoutIcon,
        onClick: () => {
          logOut();
        },
      }),
    ];
  };

  // Header views
  const conversationsHeaderView = () => {
    const userAvatar = loggedInUser?.getAvatar();
    const userName = loggedInUser?.getName();
    const isOnline = userStatus === CometChat.USER_STATUS.ONLINE;
    
    return (
      <div className="cometchat-conversations-header">
        <div className="cometchat-conversations-header__title">
          {getLocalizedString('conversation_chat_title')}
        </div>
        <div className="chat-menu">
          <div className={`user-avatar-menu ${isLoading ? 'loading' : ''}`} onClick={() => setShowUserProfile(true)}>
            {isLoading ? (
              <div className="user-avatar-loading">
                <div className="loading-spinner"></div>
              </div>
            ) : userAvatar ? (
              <>
                <img 
                  src={userAvatar} 
                  alt="User Avatar" 
                  className="user-avatar-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const placeholder = (e.target as HTMLImageElement).parentElement?.querySelector('.user-avatar-placeholder') as HTMLElement;
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                <div className={`user-status-indicator ${isOnline ? 'online' : 'offline'}`}></div>
              </>
            ) : (
              <div className="user-avatar-placeholder">
                {userName?.charAt(0)?.toUpperCase() || 'U'}
                <div className={`user-status-indicator ${isOnline ? 'online' : 'offline'}`}></div>
              </div>
            )}
          </div>
          <CometChatContextMenu
            key={`context-menu-${menuKey}`}
            closeOnOutsideClick={true}
            placement={Placement.left}
            data={getMenuOptions() as CometChatOption[]}
            topMenuSize={0}
            onOptionClicked={(e: CometChatOption) => {
              const { onClick } = e;
              if (onClick) {
                onClick();
              }
            }}
          />
        </div>
      </div>
    );
  };

  const groupsHeaderView = () => {
    return (
      <div className="cometchat-groups-header">
        <div className="cometchat-groups-header__title">{getLocalizedString('group_title')}</div>
        {!hideCreateGroupButton && (
          <CometChatButton
            onClick={() => {
              setShowCreateGroup(true);
            }}
            iconURL={createGroupIcon}
          />
        )}
      </div>
    );
  };

  // Main render
  return (
    <>
      {/* Logout Overlay */}
      {isLoggingOut && (
        <div className="logout-overlay">
          <div className="logout-overlay__content">
            <div className="logout-spinner"></div>
            <div className="logout-text">Logging out...</div>
          </div>
        </div>
      )}

      {loggedInUser && (
        <>
          {showJoinGroup && group && (
            <CometChatJoinGroup
              group={group}
              onHide={onHide}
              onProtectedGroupJoin={(group) => onProtectedGroupJoin(group)}
            />
          )}
          
          {activeTab === 'chats' ? (
            <CometChatConversations
              showSearchBar={true}
              onSearchBarClicked={onSearchClicked}
              activeConversation={activeItem as CometChat.Conversation}
              headerView={conversationsHeaderView()}
              onItemClick={(e) => {
                onSelectorItemClicked(e, 'updateSelectedItem');
              }}
              hideUserStatus={chatFeatures && !chatFeatures?.coreMessagingExperience?.userAndFriendsPresence}
              hideReceipts={chatFeatures && !chatFeatures?.coreMessagingExperience?.messageDeliveryAndReadReceipts}
            />
          ) : activeTab === 'calls' ? (
            <CometChatCallLogs
              activeCall={activeItem as CometChat.Call}
              onItemClick={(e: CometChat.Call) => {
                onSelectorItemClicked(e, 'updateSelectedItemCall');
              }}
            />
          ) : activeTab === 'users' ? (
            <CometChatUsers
              activeUser={activeItem as CometChat.User}
              onItemClick={(e) => {
                onSelectorItemClicked(e, 'updateSelectedItemUser');
              }}
              hideUserStatus={chatFeatures && !chatFeatures?.coreMessagingExperience?.userAndFriendsPresence}
            />
          ) : activeTab === 'groups' ? (
            <CometChatGroups
              activeGroup={activeItem as CometChat.Group}
              headerView={groupsHeaderView()}
              onItemClick={(e) => {
                onSelectorItemClicked(e, 'updateSelectedItemGroup');
              }}
            />
          ) : null}
          
          {showCreateGroup && (
            <CometChatCreateGroup
              setShowCreateGroup={setShowCreateGroup}
              onGroupCreated={(group) => onGroupCreated(group)}
            />
          )}
          
          {showUserProfile && (
            <CometChatUserProfile
              user={loggedInUser}
              onHide={() => setShowUserProfile(false)}
              onUserUpdated={handleUserUpdated}
            />
          )}
        </>
      )}
    </>
  );
};

const MemoizedCometChatSelector = React.memo(CometChatSelector);

export { MemoizedCometChatSelector as CometChatSelector };